import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronDown,
  Plus,
  Target,
  TrendingUp,
  Edit,
  Calendar,
  User,
  Folder,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { QUERY_KEYS } from '@/services/queryClient';
import { teamManagementService } from '@/services/api';
import { KRA, KPI, KPIType, KPIFrequency, KRAStatus } from '@/types';

/**
 * KRAKPIManagement
 * Component for managing and tracking team KRAs and KPIs
 */
const KRAKPIManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [expandedKRA, setExpandedKRA] = useState<string | false>(false);
  const [addKRAOpen, setAddKRAOpen] = useState(false);
  const [addKPIOpen, setAddKPIOpen] = useState(false);
  const [selectedKRAId, setSelectedKRAId] = useState<string>('');
  const [editingKPIId, setEditingKPIId] = useState<string | null>(null);
  const [kpiActualValue, setKpiActualValue] = useState<number>(0);

  // Fetch KRAs
  const { data: krasResponse, isLoading: krasLoading } = useQuery({
    queryKey: QUERY_KEYS.kras(),
    queryFn: async () => {
      return teamManagementService.getAllKRAs();
    },
  });

  const kras: KRA[] = krasResponse?.data || [];

  // Fetch KPIs
  const { data: kpisResponse } = useQuery({
    queryKey: QUERY_KEYS.kpis(),
    queryFn: async () => {
      return teamManagementService.getAllKPIs();
    },
  });

  const kpis: KPI[] = kpisResponse?.data || [];

  const getKPIsForKRA = (kraId: string) => {
    return kpis.filter((kpi) => kpi.kraId === kraId);
  };

  const getStatusColor = (status: KRAStatus) => {
    switch (status) {
      case KRAStatus.COMPLETED:
        return 'success';
      case KRAStatus.ON_TRACK:
        return 'primary';
      case KRAStatus.AT_RISK:
        return 'warning';
      case KRAStatus.OVERDUE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getKPIProgress = (kpi: KPI) => {
    if (kpi.type === KPIType.BOOLEAN) {
      return kpi.actualValue > 0 ? 100 : 0;
    }
    return Math.min((kpi.actualValue / kpi.targetValue) * 100, 100);
  };

  const getKPIIcon = (type: KPIType) => {
    switch (type) {
      case KPIType.PERCENTAGE:
        return '%';
      case KPIType.CURRENCY:
        return '$';
      case KPIType.RATING:
        return '⭐';
      case KPIType.BOOLEAN:
        return '✓';
      default:
        return '#';
    }
  };

  const handleAccordionChange = (kraId: string) => (_: any, isExpanded: boolean) => {
    setExpandedKRA(isExpanded ? kraId : false);
  };

  const handleAddKPI = (kraId: string) => {
    setSelectedKRAId(kraId);
    setAddKPIOpen(true);
  };

  const handleUpdateKPI = async (kpiId: string, actualValue: number) => {
    await teamManagementService.updateKPI(kpiId, actualValue);
    setEditingKPIId(null);
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.kpis() });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            KRA/KPI Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage Key Result Areas and Key Performance Indicators
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setAddKRAOpen(true)}
        >
          Add KRA
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Target size={18} style={{ color: '#2563eb' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Total KRAs
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {kras.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp size={18} style={{ color: '#16a34a' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  On Track
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {kras.filter((k) => k.status === KRAStatus.ON_TRACK).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Target size={18} style={{ color: '#f59e0b' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  At Risk
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {kras.filter((k) => k.status === KRAStatus.AT_RISK).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp size={18} style={{ color: '#8b5cf6' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Avg. Progress
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={600}>
                {kras.length > 0
                  ? Math.round(kras.reduce((sum, k) => sum + k.progress, 0) / kras.length)
                  : 0}
                %
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* KRA List */}
      {krasLoading ? (
        <Typography>Loading...</Typography>
      ) : kras.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Target size={48} style={{ color: '#9ca3af', marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No KRAs defined yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first Key Result Area to start tracking performance
            </Typography>
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setAddKRAOpen(true)}
            >
              Add KRA
            </Button>
          </CardContent>
        </Card>
      ) : (
        kras.map((kra) => (
          <Accordion
            key={kra.id}
            expanded={expandedKRA === kra.id}
            onChange={handleAccordionChange(kra.id)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary expandIcon={<ChevronDown size={20} />}>
              <Box sx={{ flex: 1, pr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {kra.title}
                  </Typography>
                  <Chip
                    label={kra.status.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(kra.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Chip label={`${kra.weight}%`} size="small" variant="outlined" />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <User size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {kra.ownerName || kra.ownerId}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Calendar size={14} />
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(kra.startDate).format('MMM D')} - {dayjs(kra.endDate).format('MMM D, YYYY')}
                    </Typography>
                  </Box>
                  {kra.relatedProjectIds.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Folder size={14} />
                      <Typography variant="caption" color="text.secondary">
                        {kra.relatedProjectIds.length} projects
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption">Progress</Typography>
                    <Typography variant="caption" fontWeight={600}>
                      {kra.progress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={kra.progress}
                    color={
                      kra.progress >= 75 ? 'success' : kra.progress >= 50 ? 'primary' : 'warning'
                    }
                  />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {/* KRA Description */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {kra.description}
                </Typography>

                {/* KPIs Section */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Key Performance Indicators ({getKPIsForKRA(kra.id).length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Plus size={14} />}
                    onClick={() => handleAddKPI(kra.id)}
                  >
                    Add KPI
                  </Button>
                </Box>

                {/* KPI Cards */}
                <Grid container spacing={2}>
                  {getKPIsForKRA(kra.id).map((kpi) => (
                    <Grid item xs={12} sm={6} md={4} key={kpi.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                                {getKPIIcon(kpi.type)}
                              </Typography>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {kpi.title}
                              </Typography>
                            </Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingKPIId(kpi.id);
                                  setKpiActualValue(kpi.actualValue);
                                }}
                              >
                                <Edit size={14} />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Target: {kpi.targetValue} {kpi.unit}
                            </Typography>
                            <br />
                            <Typography variant="caption" fontWeight={600}>
                              Actual: {kpi.actualValue} {kpi.unit}
                            </Typography>
                          </Box>

                          <Box sx={{ mb: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={getKPIProgress(kpi)}
                              color={
                                getKPIProgress(kpi) >= 100
                                  ? 'success'
                                  : getKPIProgress(kpi) >= 50
                                  ? 'primary'
                                  : 'warning'
                              }
                            />
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                              {kpi.frequency}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(getKPIProgress(kpi))}%
                            </Typography>
                          </Box>

                          {/* Edit Mode */}
                          {editingKPIId === kpi.id && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Actual Value"
                                value={kpiActualValue}
                                onChange={(e) => setKpiActualValue(Number(e.target.value))}
                              />
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleUpdateKPI(kpi.id, kpiActualValue)}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => setEditingKPIId(null)}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {getKPIsForKRA(kra.id).length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      No KPIs defined for this KRA
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Add KRA Dialog */}
      <Dialog open={addKRAOpen} onClose={() => setAddKRAOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New KRA</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label="Title" />
            <TextField fullWidth label="Description" multiline rows={3} />
            <TextField fullWidth label="Owner" select>
              <MenuItem value="user1">User 1</MenuItem>
            </TextField>
            <TextField fullWidth label="Weight (%)" type="number" />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="End Date" type="date" InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddKRAOpen(false)}>Cancel</Button>
          <Button variant="contained">Create KRA</Button>
        </DialogActions>
      </Dialog>

      {/* Add KPI Dialog */}
      <Dialog open={addKPIOpen} onClose={() => setAddKPIOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New KPI</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label="Title" />
            <TextField fullWidth label="Description" multiline rows={2} />
            <TextField fullWidth label="Type" select>
              <MenuItem value={KPIType.PERCENTAGE}>Percentage</MenuItem>
              <MenuItem value={KPIType.NUMBER}>Number</MenuItem>
              <MenuItem value={KPIType.CURRENCY}>Currency</MenuItem>
              <MenuItem value={KPIType.BOOLEAN}>Boolean</MenuItem>
              <MenuItem value={KPIType.RATING}>Rating</MenuItem>
            </TextField>
            <TextField fullWidth label="Frequency" select>
              <MenuItem value={KPIFrequency.DAILY}>Daily</MenuItem>
              <MenuItem value={KPIFrequency.WEEKLY}>Weekly</MenuItem>
              <MenuItem value={KPIFrequency.MONTHLY}>Monthly</MenuItem>
              <MenuItem value={KPIFrequency.QUARTERLY}>Quarterly</MenuItem>
              <MenuItem value={KPIFrequency.YEARLY}>Yearly</MenuItem>
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Target Value" type="number" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Unit (optional)" />
              </Grid>
            </Grid>
            <TextField fullWidth label="Measurement Criteria" multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddKPIOpen(false)}>Cancel</Button>
          <Button variant="contained">Create KPI</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KRAKPIManagement;
